// frontend/src/features/inventory/pages/LabelSettingsPage.tsx
// ADR-099 Rule LBL-05b.
// نام دقیق هوک‌های orval (useV1InventoryLabelPrintSettingsList/Create/Update) باید
// بعد از اجرای واقعی orval (npm run api:generate) از v1.ts تأیید شود — همان الگوی
// همیشگی پروژه (چک قبل از استفاده، نه فرض).

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useV1InventoryLabelPrintSettingsList,
  useV1InventoryLabelPrintSettingsCreate,
  useV1InventoryLabelPrintSettingsUpdate,
} from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";

type TemplateKind = "compact" | "full";

const DEFAULTS: Record<TemplateKind, { width: string; height: string }> = {
  compact: { width: "50", height: "30" },
  full: { width: "100", height: "150" },
};

export function LabelSettingsPage() {
  const navigate = useNavigate();
  const [activeKind, setActiveKind] = useState<TemplateKind>("compact");
  const [width, setWidth] = useState(DEFAULTS.compact.width);
  const [height, setHeight] = useState(DEFAULTS.compact.height);
  const [layoutJson, setLayoutJson] = useState<string>("{}");
  const [savedId, setSavedId] = useState<string | null>(null);

  const { data: listData } = useV1InventoryLabelPrintSettingsList(undefined, { query: {} });
  const { mutate: create, isPending: isCreating } = useV1InventoryLabelPrintSettingsCreate();
  const { mutate: update, isPending: isUpdating } = useV1InventoryLabelPrintSettingsUpdate();

  useEffect(() => {
    const existing = (listData?.results ?? []).find(
      (s: any) => s.template_kind === activeKind
    );
    if (existing) {
      setSavedId(existing.id);
      setWidth(String(existing.paper_width_mm));
      setHeight(String(existing.paper_height_mm));
      setLayoutJson(JSON.stringify(existing.layout_json ?? {}, null, 2));
    } else {
      setSavedId(null);
      setWidth(DEFAULTS[activeKind].width);
      setHeight(DEFAULTS[activeKind].height);
      setLayoutJson("{}");
    }
  }, [activeKind, listData]);

  const handleSave = () => {
    let parsedLayout: Record<string, unknown>;
    try {
      parsedLayout = JSON.parse(layoutJson || "{}");
    } catch {
      alert("فرمت JSON چیدمان معتبر نیست.");
      return;
    }

    const payload = {
      template_kind: activeKind,
      paper_width_mm: width,
      paper_height_mm: height,
      layout_json: parsedLayout,
    };

    if (savedId) {
      update({ id: savedId, data: payload as any });
    } else {
      create({ data: payload as any });
    }
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="تنظیمات چیدمان برچسب پالت"
        subtitle="طراحی و مدیریت لیبل‌های چاپگر بارکد"
        icon="🖨️"
        helpLink="/help/inventory/label-settings"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/inventory")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← داشبورد انبار
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "700px", margin: "0 auto", padding: "2rem", width: "100%" }}>

        <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.5rem" }}>
          <button
            className={activeKind === "compact" ? "btn btn-primary" : "btn btn-secondary"}
            onClick={() => setActiveKind("compact")}
          >
            Compact (فقط QR)
          </button>
          <button
            className={activeKind === "full" ? "btn btn-primary" : "btn btn-secondary"}
            onClick={() => setActiveKind("full")}
          >
            Full (کامل)
          </button>
        </div>

        <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
              عرض (mm)
            </label>
            <input
              type="number"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              dir="ltr"
              style={{ width: "100%", padding: "0.6rem 0.9rem", borderRadius: "8px" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
              ارتفاع (mm)
            </label>
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              dir="ltr"
              style={{ width: "100%", padding: "0.6rem 0.9rem", borderRadius: "8px" }}
            />
          </div>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
            چیدمان پیشرفته (JSON — اختیاری، برای v1 آزاد نگه‌داشته شده)
          </label>
          <textarea
            value={layoutJson}
            onChange={(e) => setLayoutJson(e.target.value)}
            rows={8}
            dir="ltr"
            style={{
              width: "100%",
              padding: "0.6rem 0.9rem",
              borderRadius: "8px",
              fontFamily: "monospace",
              boxSizing: "border-box",
            }}
          />
        </div>

        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isCreating || isUpdating}
        >
          {isCreating || isUpdating ? "در حال ذخیره..." : "ذخیره تنظیمات"}
        </button>
      </div>
    </div>
  );
}
